import { Skeleton } from '@ircf/ui';

/**
 * Suspense fallback while a lazily-imported route chunk downloads.
 *
 * Two variants, because the right fallback differs by context. Public routes get
 * a dark hero-shaped placeholder that matches the cinematic layout, so the
 * transition does not flash white. Portal routes get a page-header-plus-cards
 * skeleton that matches the operational layout.
 *
 * Both announce politely rather than assertively: a route change is expected, so
 * it should not interrupt a screen reader mid-sentence.
 */
export function RouteFallback({ variant = 'public' }: { variant?: 'public' | 'portal' }) {
  if (variant === 'portal') {
    return (
      <div className="space-y-6 p-4 sm:p-6" role="status" aria-live="polite" aria-busy="true">
        <span className="sr-only">Loading page…</span>
        <div className="space-y-3">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-4 w-96 max-w-full" />
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2, 3, 4, 5].map((index) => (
            <Skeleton key={index} className="h-32 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex min-h-[70vh] flex-col justify-center gap-6 px-6 py-24 sm:px-10"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <span className="sr-only">Loading page…</span>
      <Skeleton className="h-4 w-32" />
      <Skeleton className="h-16 w-full max-w-3xl" />
      <Skeleton className="h-16 w-full max-w-2xl" />
      <Skeleton className="h-5 w-full max-w-xl" />
      <div className="flex flex-wrap gap-4 pt-4">
        <Skeleton className="h-12 w-44 rounded-full" />
        <Skeleton className="h-12 w-44 rounded-full" />
      </div>
    </div>
  );
}
